import React, { useEffect, useState } from 'react';
import { Box, Button, Collapse, Dialog, Stack, SvgIcon, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { useSnackbar } from 'components/snackbar';
import { useForm } from 'react-hook-form';

import { addSummary } from 'api/users';

import RemoveCard from 'components/job-application/RemoveCard';

/**
 * @function ProfessionalSummary
 * @description Component for rendering the professional summary section on the profile page.
 * @param {boolean} setUpdating - Whether the data is being updated or not.
 * @param {string} summaryData - The summary data.
 * @param {function} onSummaryDataChange - The function to call when the summary data changes.
 * @param {boolean} Summary - Whether the summary is being edited or not.
 * @param {function} handleSummary - The function to call when the summary changes.
 * @param {function} handleExpand - The function to call when the summary is expanded.
 * @returns {ReactElement} The component for the professional summary section.
 */
const ProfessionalSummary = ({ setUpdating, summaryData, onSummaryDataChange, Summary, handleSummary, handleExpand }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [showProfSummary, setShowProfSummary] = useState(!!summaryData);
  const [summaryText, setSummaryText] = useState(summaryData || '');
  const [openRemoveCard, setOpenRemoveCard] = useState(false);
  const [timeoutId, setTimeoutId] = useState(null);

  const defaultValues = {
    summary: '',
  };

  const methods = useForm({
    defaultValues,
    mode: 'onChange',
  });

  const { setValue, handleSubmit } = methods;

  useEffect(() => {
    setShowProfSummary(!!summaryData);
    setSummaryText(summaryData || '');
  }, []);

  const onSubmit = handleSubmit(async (data) => {
    try {
      setUpdating(true);
      if (Summary) {
        handleSummary("Professional Summary", data);
        return;
      }
      const response = await addSummary(data);
      if (response?.success === true && response.data) {
        handleExpand();
      } else if (response?.success === false) {
        enqueueSnackbar(response.message, { variant: 'error' });
      }
    } catch (e) {
      console.error(e);
      setUpdating(false);
    }
  });

  /**
   * @function handleSummaryChange
   * @description Handles the change event in the Professional Summary text field.
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event.
   * @returns {void}
   */
  const handleSummaryChange = (e) => {
    const value = e.target.value;
    setSummaryText(value);
    setValue('summary', value);
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    onSummaryDataChange(value);
    const newTimeoutId = setTimeout(() => {
      onSubmit({ summary: value });
    }, 1000);
    setTimeoutId(newTimeoutId);
  };

  /**
   * @function handleRemoveSummary
   * @description Handles the removal of the Professional Summary.
   * @returns {void}
   */
  const handleRemoveSummary = async () => {
    try {
      setUpdating(true);
      const response = await addSummary({ summary: '' });
      if (response?.success === true && response.data) {
        handleRemoveCard();
        onSummaryDataChange();
        setShowProfSummary(response.data.summary);
      } else if (response?.success === false) {
        enqueueSnackbar(response.message, { variant: 'error' });
      }
    } catch (e) {
      setUpdating(false);
    }
  };

  /**
   * @function handleShowProfSummary
   * @description Handles the showing of the Professional Summary.
   * @returns {void}
   */
  const handleShowProfSummary = () => {
    setShowProfSummary(true);
  };

  /**
   * @function handleRemoveCard
   * @description Closes the remove card dialog.
   * @returns {void}
   */

  const handleRemoveCard = () => {
    setOpenRemoveCard(false);
  };

  /**
   * @function checkData
   * @description Checks if the summary data has been added and shows/hides the
   *              Professional Summary section accordingly.
   * @returns {void}
   */
  const checkData = () => {
    if (typeof summaryData !== 'undefined' && summaryData !== '') {
      setShowProfSummary(true);
    } else {
      setShowProfSummary(false);
    }
  };

  const aiIcon = (
    <Box className="ai-icon">
      <SvgIcon>
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4.5142 2.09567C4.36799 1.73006 4.10093 1.42557 3.7575 1.23294C3.7181 1.21062 3.68832 1.17453 3.67385 1.13162C3.65939 1.08872 3.66125 1.04199 3.6791 1.00037C3.69694 0.958759 3.7295 0.925188 3.77055 0.906087C3.81161 0.886986 3.85826 0.883697 3.90159 0.896851C4.2779 1.01274 4.68257 0.996206 5.04818 0.849992C5.41379 0.703778 5.71826 0.436715 5.91089 0.0932881C5.9332 0.0538906 5.96927 0.0241077 6.01217 0.0096412C6.05508 -0.00482527 6.10181 -0.00295875 6.14342 0.0148835C6.18504 0.0327257 6.21861 0.0652898 6.23771 0.106341C6.25681 0.147393 6.2601 0.194048 6.24694 0.237373C6.13106 0.613704 6.1476 1.01838 6.29382 1.384C6.44004 1.74962 6.7071 2.0541 7.05054 2.24674C7.08994 2.26905 7.11972 2.30511 7.13418 2.34802C7.14865 2.39092 7.14679 2.43766 7.12894 2.47927C7.1111 2.52088 7.07854 2.55446 7.03749 2.57356C6.99643 2.59266 6.94978 2.59595 6.90645 2.58279C6.53013 2.46689 6.12545 2.48342 5.75984 2.62963C5.39422 2.77585 5.08974 3.04292 4.89712 3.38636C4.8748 3.42575 4.83874 3.45554 4.79584 3.47C4.75293 3.48447 4.7062 3.4826 4.66459 3.46476C4.62297 3.44692 4.5894 3.41435 4.5703 3.3733C4.5512 3.33225 4.54791 3.2856 4.56106 3.24227C4.67695 2.86595 4.66041 2.46128 4.5142 2.09567ZM11.1913 0.535699C11.023 0.652745 10.8196 0.708584 10.6151 0.693913C10.4107 0.679242 10.2174 0.594948 10.0675 0.455073C10.0342 0.424329 9.99084 0.40692 9.94557 0.406181C9.9003 0.405441 9.85636 0.421421 9.82214 0.451063C9.78792 0.480706 9.76583 0.521927 9.76011 0.566836C9.75438 0.611746 9.76542 0.657189 9.79111 0.694468C9.90814 0.862789 9.96397 1.06613 9.9493 1.27061C9.93463 1.47509 9.85035 1.66838 9.71048 1.81827C9.67976 1.8515 9.66238 1.89489 9.66164 1.94015C9.66091 1.9854 9.67689 2.02933 9.70652 2.06354C9.73615 2.09775 9.77735 2.11984 9.82225 2.12557C9.86714 2.13131 9.91257 2.12029 9.94985 2.09463C10.1182 1.97761 10.3215 1.92179 10.526 1.93646C10.7305 1.95113 10.9238 2.03541 11.0737 2.17526C11.1069 2.20598 11.1503 2.22337 11.1956 2.2241C11.2408 2.22483 11.2847 2.20885 11.319 2.17922C11.3532 2.14959 11.3752 2.10839 11.381 2.0635C11.3867 2.01861 11.3757 1.97317 11.35 1.93589C11.233 1.76757 11.1772 1.56423 11.1919 1.35974C11.2065 1.15525 11.2908 0.961963 11.4307 0.812065C11.4614 0.778831 11.4788 0.735437 11.4795 0.690185C11.4802 0.644934 11.4643 0.601001 11.4346 0.566792C11.405 0.532582 11.3638 0.510496 11.3189 0.50476C11.274 0.499023 11.2286 0.510039 11.1913 0.535699ZM8.40241 3.47278L7.75777 4.11742L8.40239 4.76204L9.04703 4.1174L8.40241 3.47278ZM7.49747 4.37736L0.519531 11.3553L1.16417 11.9999L8.14211 5.022L7.49747 4.37736ZM10.4631 6.73775C10.6344 6.96857 10.8796 7.13373 11.1579 7.20573C11.2017 7.21721 11.2398 7.24446 11.2648 7.28227C11.2898 7.32007 11.2999 7.36576 11.2933 7.41059C11.2866 7.45541 11.2637 7.49621 11.2288 7.52516C11.194 7.55411 11.1497 7.56918 11.1044 7.56747C10.8172 7.55567 10.5346 7.64268 10.3037 7.81399C10.0729 7.9853 9.90774 8.23057 9.83583 8.5089C9.82435 8.55273 9.7971 8.59078 9.75929 8.61576C9.72149 8.64074 9.67579 8.65088 9.63097 8.64425C9.58615 8.63762 9.54535 8.61468 9.5164 8.57982C9.48745 8.54496 9.47238 8.50065 9.47409 8.45537C9.48579 8.16817 9.39875 7.88562 9.22745 7.6548C9.05615 7.42398 8.81094 7.25881 8.53266 7.18681C8.48883 7.17533 8.45077 7.14808 8.42579 7.11027C8.40081 7.07246 8.39067 7.02676 8.3973 6.98194C8.40394 6.93711 8.42689 6.89631 8.46175 6.86736C8.49662 6.83841 8.54094 6.82335 8.58622 6.82507C8.87342 6.83679 9.15597 6.74975 9.38679 6.57845C9.61761 6.40715 9.78277 6.16193 9.85475 5.88364C9.86623 5.83981 9.89348 5.80175 9.93129 5.77677C9.9691 5.75179 10.0148 5.74165 10.0596 5.74828C10.1045 5.75492 10.1452 5.77787 10.1742 5.81273C10.2031 5.8476 10.2182 5.89192 10.2165 5.9372C10.2048 6.2244 10.2918 6.50694 10.4631 6.73775Z"
            fill="white"
          />
        </svg>
      </SvgIcon>
    </Box>
  );
  return (
    <Box className="prof-summary-wrapper">
      <Stack direction="row" justifyContent={'space-between'} spacing={2}>
        <Stack direction="row" alignItems={'center'} spacing={1.5} >
          <Box mb={3}>
            <Typography className="step-heading" variant="h6" gutterBottom>
              Your Professional Summary (Optional)
            </Typography>
            <Typography className="step-info" variant="body2" gutterBottom>
              {Summary ? 'This is like a 10 second elevator pitch to recruiters' : 'Please keep this brief. This is like an elevator pitch to recruiters.'}
            </Typography>
          </Box>
        </Stack>
      </Stack>
      <Collapse in={showProfSummary}>
        <Box mb={1}>
          <TextField
            multiline
            variant="outlined"
            {...methods.register('summary')}
            fullWidth
            value={summaryText}
            onChange={handleSummaryChange}
          />
        </Box>
      </Collapse>
      {!showProfSummary && (
        <Box mt={2}>
          <Button className="add-btn" startIcon={<AddIcon />} onClick={handleShowProfSummary} disableRipple>
            Add Professional Summary
          </Button>
        </Box>
      )
      }
      <Dialog
        open={openRemoveCard}
        onClose={handleRemoveCard}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className="otp-dialog-wrapper"
      >
        <RemoveCard onClose={handleRemoveCard} onRemove={handleRemoveSummary} title={"professional summary"} section={"Summary"} />
      </Dialog>
    </Box >
  );
};

export default ProfessionalSummary;
